import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import { ARTICLE_ARCHITECT_EVENTS } from '@conversed/shared/src/central/constants/socket';
import type {
  ArticleArchitectCompletedData,
  ArticleArchitectErrorData,
  ArticleArchitectProgressData,
  ArticleArchitectStartPayload,
  ConflictScanCompletedData,
  ConflictScanProgressData,
} from '@conversed/shared/src/central/dto/article-architect.dto';
import { useMainSelector } from '@hooks/useMainSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

const { PLATFORM_TO_BACKEND, BACKEND_TO_PLATFORM } = ARTICLE_ARCHITECT_EVENTS;

export const useArticleArchitectSocket = (
  onProgress?: (data: ArticleArchitectProgressData) => void,
  onCompleted?: (data: ArticleArchitectCompletedData) => void,
  onError?: (data: ArticleArchitectErrorData) => void,
  onConflictScanProgress?: (data: ConflictScanProgressData) => void,
  onConflictScanCompleted?: (data: ConflictScanCompletedData) => void,
) => {
  const {
    login: { accessToken },
  } = useMainSelector();

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Store callbacks in refs to avoid reconnecting when they change
  const onProgressRef = useRef(onProgress);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  const onConflictScanProgressRef = useRef(onConflictScanProgress);
  const onConflictScanCompletedRef = useRef(onConflictScanCompleted);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onConflictScanProgressRef.current = onConflictScanProgress;
  }, [onConflictScanProgress]);

  useEffect(() => {
    onConflictScanCompletedRef.current = onConflictScanCompleted;
  }, [onConflictScanCompleted]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: {
        token: accessToken,
      },
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });

    socketRef.current.on(
      'connect_error',
      (err: Error & { data?: { 'retry-ms'?: number; reason?: string } }) => {
        const { data } = err;
        const retryMs = data?.['retry-ms'];
        setIsConnected(false);

        if (typeof retryMs === 'number') {
          console.error(
            `[ArticleArchitect] WebSocket rate limited, retry in ${retryMs / 1000}s`,
          );
          return;
        }

        if (data?.reason) {
          console.error(`[ArticleArchitect] WebSocket blocked: ${data.reason}`);
          return;
        }

        console.error('[ArticleArchitect] Unknown WebSocket connection error');
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.PROGRESS,
      (data: ArticleArchitectProgressData) => {
        onProgressRef.current?.(data);
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.COMPLETED,
      (data: ArticleArchitectCompletedData) => {
        onCompletedRef.current?.(data);
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.ERROR,
      (data: ArticleArchitectErrorData) => {
        onErrorRef.current?.(data);
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.CONFLICT_SCAN_PROGRESS,
      (data: ConflictScanProgressData) => {
        onConflictScanProgressRef.current?.(data);
      },
    );

    socketRef.current.on(
      BACKEND_TO_PLATFORM.CONFLICT_SCAN_COMPLETED,
      (data: ConflictScanCompletedData) => {
        onConflictScanCompletedRef.current?.(data);
      },
    );

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [accessToken]);

  const startGeneration = useCallback(
    (payload: ArticleArchitectStartPayload) => {
      if (!socketRef.current?.connected) {
        console.error('[ArticleArchitect] Socket not connected');
        return false;
      }

      socketRef.current.emit(PLATFORM_TO_BACKEND.START_GENERATION, payload);
      return true;
    },
    [],
  );

  const startBccBatch = useCallback((companyId: string) => {
    if (!socketRef.current?.connected) {
      console.error('[ArticleArchitect] Socket not connected');
      return false;
    }

    socketRef.current.emit(PLATFORM_TO_BACKEND.START_BCC_BATCH, { companyId });
    return true;
  }, []);

  return {
    startGeneration,
    startBccBatch,
    isConnected,
  };
};
